import { AgilityExportButtonModule } from './components/agility-export-button/agility-export-button.module';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { CarouselModule } from 'primeng/carousel';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PickListModule } from 'primeng/picklist';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RippleModule } from 'primeng/ripple';
import { DrawerModule } from 'primeng/drawer';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TreeTableModule } from 'primeng/treetable';
import { ChipModule } from 'primeng/chip';
import { FluidModule } from 'primeng/fluid';

import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';

import { CustomEventsService } from './services/custom-events/custom-events.service';
import { I18nService } from './services/i18n/i18n.service';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { CustomEventTokens } from './models/custom-event-tokens';

import { AuthGuard } from '@auth0/auth0-angular';
import { AccordionModule } from 'primeng/accordion';
import { BadgeModule } from 'primeng/badge';
import { ChartModule } from 'primeng/chart';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FileUploadModule } from 'primeng/fileupload';
import { TextareaModule } from 'primeng/textarea';
import { PopoverModule } from 'primeng/popover';
import { TimelineModule } from 'primeng/timeline';
import { CustomModule } from '../custom/custom.module';
import { GraphQLModule } from '../gql-apollo/graphql.module';
import { GlobalLoanCardComponent } from './components/global-loan-card/global-loan-card.component';
import { GlobalLoanSearchComponent } from './components/global-loan-search/global-loan-search.component';
import { MobileNavigationComponent } from './components/mobile-navigation/mobile-navigation.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { UpcomingEventComponent } from './components/upcoming-event/upcoming-event.component';
import { UserRolesComponent } from './components/user-roles/user-roles.component';
import { CalenderMaskDirective } from './directives/calendar-mask/calendar-mask.directive';
import { AdminGuard } from './guards/admin/admin.guard';
import { PermissionService } from './guards/permission/service/permission.service';
import { FileSizePipe } from './pipes/fiile-size/file-size.pipe';
import { IndexofPipe } from './pipes/indexof.pipe';

import { PluralizePipe } from './pipes/pluralize/pluralize.pipe';
import { WireRecipientService } from './services/wire-recipient/wire-recipient.service';
import { CsvService } from './services/csv/csv.service';
import { FundingDashboardService } from './services/dashboard/funding-dashboard.service';
import { SidebarService } from './services/dashboard/sidebar.service';
import { EnumerationsService } from './services/enumerations/enumerations.service';
import { JwtService } from './services/jwt.service';
import { LoaderService } from './services/loader/loader.service';
import { LoanConditionsService } from './services/loan-conditions.service';
import { LoanSearchService } from './services/loan-search/loan-search.service';
import { ToastMessageService } from './services/toast-message.service';
import { UserService } from './services/user.service';
import { UtilsService } from './services/utils/utils.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { DateTimeDisplayPipe } from './pipes/date-time-display/date-time-display.pipe';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AgilityButtonComponent } from '@agility-components/agility-button/agility-button.component';
import { AgilityCurrencyComponent } from '../custom/agility-currency/agility-currency.component';
import { CreateReportComponent } from './components/create-report/create-report.component';

@NgModule({
  declarations: [
    HeaderComponent,
    FooterComponent,
    PageNotFoundComponent,
    NavigationComponent,
    MobileNavigationComponent,
    UpcomingEventComponent,
    FileSizePipe,
    PluralizePipe,
    IndexofPipe,
    UserRolesComponent,
    CalenderMaskDirective,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CardModule,
    RippleModule,
    ButtonModule,
    InputTextModule,
    DrawerModule,
    ToastModule,
    ConfirmDialogModule,
    HeaderComponent,
    FooterComponent,
    PageNotFoundComponent,
    TabsModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    MessageModule,
    InputNumberModule,
    CheckboxModule,
    RadioButtonModule,
    IconFieldModule,
    InputIconModule,
    InputMaskModule,
    ToggleSwitchModule,
    MenuModule,
    TableModule,
    TreeTableModule,
    PickListModule,
    FileUploadModule,
    GraphQLModule,
    TranslateModule,
    UpcomingEventComponent,
    AccordionModule,
    FileSizePipe,
    PluralizePipe,
    IndexofPipe,
    TextareaModule,
    AutoCompleteModule,
    TimelineModule,
    DividerModule,
    SkeletonModule,
    BadgeModule,
    CarouselModule,
    PopoverModule,
    UserRolesComponent,
    CustomModule,
    CalenderMaskDirective,
    InfiniteScrollDirective,
    ChipModule,
    FluidModule,
  ],
  imports: [
    AgilityButtonComponent,
    DateTimeDisplayPipe,
    AgilityExportButtonModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    RouterModule,
    CardModule,
    RippleModule,
    ButtonModule,
    InputTextModule,
    DrawerModule,
    ToastModule,
    ConfirmDialogModule,
    TabsModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    MessageModule,
    InputNumberModule,
    CheckboxModule,
    RadioButtonModule,
    InputMaskModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
    MenuModule,
    TableModule,
    TreeTableModule,
    TooltipModule,
    PickListModule,
    FileUploadModule,
    GraphQLModule,
    AccordionModule,
    TextareaModule,
    AutoCompleteModule,
    TimelineModule,
    DividerModule,
    ChartModule,
    SkeletonModule,
    BadgeModule,
    CarouselModule,
    PopoverModule,
    ChipModule,
    CustomModule,
    InfiniteScrollDirective,
    TranslateModule.forRoot({
      defaultLanguage: CustomEventTokens.ENGLISH_LANGUAGE,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    GlobalLoanCardComponent,
    GlobalLoanSearchComponent,
    FluidModule,
    AgilityCurrencyComponent,
    CreateReportComponent,
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        MessageService,
        ConfirmationService,
        CustomEventsService,
        LocalStorageService,
        EnumerationsService,
        AuthGuard,
        AdminGuard,
        I18nService,
        UtilsService,
        LoanSearchService,
        DialogService,
        DynamicDialogRef,
        DynamicDialogConfig,
        LoanConditionsService,
        LoanSearchService,
        WireRecipientService,
        DatePipe,
        CurrencyPipe,
        FileSizePipe,
        CsvService,
        ToastMessageService,
        UserService,
        LoaderService,
        JwtService,
        PermissionService,
        SidebarService,
        FundingDashboardService,
      ],
    };
  }
}
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
